import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography,
} from '@mui/material';
import SerialNumbersField from './SerialNumbersField';
import { useSerialTrackedItems } from './useSerialTrackedItems';
import { toLineSerials } from '../../services/serial.service';
import { LineSerialsRequest } from '../../interfaces/inventory/serial.interface';

export interface SerialPostLine {
  lineId: string;
  itemId?: string | null;
  label: string;
  quantity: number;
  mode: 'capture' | 'pick';
  /** pick: the store whose IN_STOCK serials are offered. */
  storeId?: string | null;
  /** pick: fixed choices. */
  options?: string[];
  /** pick: offer this item's serials SOLD on this challan (sales return). */
  soldOnChallanId?: string | null;
}

interface Props {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  lines: SerialPostLine[];
  submitting?: boolean;
  onCancel: () => void;
  /** `serials` is undefined when no line carries any. */
  onConfirm: (serials?: LineSerialsRequest) => void;
}

/**
 * F6b: the confirm step of a stock-moving document. For each serial-tracked line it asks for one serial per unit
 * (capture or pick); lines of untracked items show nothing, so for them it is a plain confirm dialog.
 */
const SerialPostDialog: React.FC<Props> = ({ open, title, message, confirmLabel = 'Confirm', lines, submitting, onCancel, onConfirm }) => {
  const { isTracked } = useSerialTrackedItems(lines.map((l) => l.itemId));
  const [byLine, setByLine] = useState<Record<string, string[]>>({});
  useEffect(() => { if (open) setByLine({}); }, [open]);

  const shown = lines.filter((l) => l.itemId && isTracked(l.itemId) !== false);
  const hasPicks = Object.values(byLine).some((v) => v.length > 0);
  const incomplete = shown.some((l) => isTracked(l.itemId) === true && (byLine[l.lineId]?.length ?? 0) !== l.quantity);

  return (
    <Dialog
      open={open}
      // polish 4: Esc / a backdrop click must not throw away picked serials; only Cancel discards them
      onClose={submitting || hasPicks ? undefined : onCancel}
      data-help-page="store.serialPost.page"
      disableEscapeKeyDown={submitting || hasPicks}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {message && <DialogContentText sx={{ mb: shown.length ? 2 : 0 }}>{message}</DialogContentText>}
        {shown.map((l) => (
          <Box key={l.lineId} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{l.label} &times; {l.quantity}</Typography>
            <SerialNumbersField
              mode={l.mode}
              expected={l.quantity}
              value={byLine[l.lineId] ?? []}
              onChange={(v) => setByLine((prev) => ({ ...prev, [l.lineId]: v }))}
              itemId={l.itemId}
              storeId={l.storeId}
              options={l.options}
              soldOnChallanId={l.soldOnChallanId}
              optional={isTracked(l.itemId) === undefined}
              disabled={submitting}
              data-testid={`serials-${l.lineId}`}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button data-help="store.serialPost.cancel" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(toLineSerials(byLine))}
          disabled={submitting || incomplete}
          data-help="store.serialPost.confirm"
          data-testid="serial-post-confirm"
        >
          {submitting ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SerialPostDialog;
